import { useCallback, useEffect, useState } from "react";
import { Toaster } from "sonner";
import { ArrowLeft, AlertTriangle, Headset } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  prototypeApi,
  getToken,
  clearToken,
  getProjectId,
  setProjectId,
} from "@/lib/prototypeApi";
import { PrototypeGate } from "@/components/prototype/PrototypeGate";
import { AppHeader } from "@/components/prototype/AppHeader";
import { ProjectsPage } from "@/components/prototype/ProjectsPage";
import { Wizard } from "@/components/prototype/Wizard";
import { OwnerDiagnostics } from "@/components/prototype/OwnerDiagnostics";
import { VideosPanel } from "@/components/prototype/VideosPanel";

function FailedView({ onBack }) {
  return (
    <div className="mx-auto max-w-xl rounded-3xl border border-re-line bg-white p-8 text-center" data-testid="failed-view">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-re-red">
        <AlertTriangle className="h-7 w-7" />
      </div>
      <h2 className="mt-4 font-heading text-2xl font-bold text-re-charcoal">Generatie mislukt</h2>
      <p className="mt-2 text-sm text-re-muted">Er ging iets mis tijdens het genereren.</p>
      <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-re-charcoal">
        <Headset className="h-4 w-4 text-re-red" /> Neem contact op met Real Estate Pro support.
      </p>
      <Button variant="outline" onClick={onBack} data-testid="failed-back" className="mt-6 rounded-full">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Terug naar projecten
      </Button>
    </div>
  );
}

function CompletedView({ project, onBack }) {
  const address = project.draft?.address?.formattedAddress || project.address || "Videoproject";
  return (
    <div className="space-y-6" data-testid="completed-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-re-charcoal">Je video&apos;s zijn klaar</h2>
          <p className="text-sm text-re-muted">{address}</p>
        </div>
        <Button variant="outline" onClick={onBack} data-testid="completed-back" className="rounded-full">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Terug naar projecten
        </Button>
      </div>
      <VideosPanel listingId={project.listingId} videos={project.videos || []} />
    </div>
  );
}

export default function Prototype() {
  const [authed, setAuthed] = useState(!!getToken());
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [view, setView] = useState("projects"); // projects | wizard | completed | failed
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [wizardIsNew, setWizardIsNew] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!authed) return;
    (async () => {
      try { setConfig((await prototypeApi.config()).data); } catch { /* */ }
      try { setPresets((await prototypeApi.presets()).data.presets || []); } catch { /* */ }
    })();
  }, [authed]);

  useEffect(() => {
    const onLogout = () => setAuthed(false);
    window.addEventListener("prototype-logout", onLogout);
    return () => window.removeEventListener("prototype-logout", onLogout);
  }, []);

  const logout = () => { clearToken(); setAuthed(false); };

  const goProjects = useCallback(() => { setReloadKey((k) => k + 1); setView("projects"); }, []);

  const newProject = () => {
    const pid = crypto.randomUUID();
    setProjectId(pid);
    setCurrentProjectId(pid);
    setWizardIsNew(true);
    setView("wizard");
  };

  const openProject = (p) => {
    if (p.listingStatus === "success") { setCurrentProject(p); setView("completed"); }
    else if (p.listingStatus === "error") { setCurrentProject(p); setView("failed"); }
    else { setProjectId(p.localProjectId); setCurrentProjectId(p.localProjectId); setWizardIsNew(false); setView("wizard"); }
  };

  if (!authed) return <PrototypeGate onAuthed={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-re-soft">
      <Toaster position="top-center" richColors />
      <AppHeader onGoProjects={goProjects} onOpenDiagnostics={() => setDiagOpen(true)} onLogout={logout} />

      <main className="mx-auto max-w-6xl px-5 py-8">
        {view === "projects" && <ProjectsPage onNewProject={newProject} onOpenProject={openProject} reloadKey={reloadKey} />}
        {view === "wizard" && currentProjectId && (
          <Wizard localProjectId={currentProjectId} config={config} presets={presets} onExit={goProjects} isNew={wizardIsNew} />
        )}
        {view === "completed" && currentProject && <CompletedView project={currentProject} onBack={goProjects} />}
        {view === "failed" && <FailedView onBack={goProjects} />}
      </main>

      <OwnerDiagnostics open={diagOpen} onOpenChange={setDiagOpen} />
    </div>
  );
}
